import React, { ReactNode, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery } from '@tanstack/react-query';
import { commonService } from '@/services/commonService';
import { CountryContext, CountryContextValue } from './CountryContext';
import {
  QUERY_CONFIG,
  FALLBACK_COUNTRIES,
  sanitizePhoneInput,
  createCountryLookup,
  findCountryByCodeFromLookup,
  findCountryCodeByName,
  validatePhoneLength,
  formatCountryCodeDisplay,
  getMaxPhoneLength,
  getValidationMessage,
} from './utils';

interface CountryProviderProps {
  children: ReactNode;
}

// ================================
// PROVIDER COMPONENT
// ================================

export const CountryProvider: React.FC<CountryProviderProps> = ({
  children,
}) => {
  // Fetch country codes with optimized query configuration
  const {
    data: countryCodesResponse,
    isLoading,
    error,
    refetch,
  } = useQuery({
    queryKey: ['countryCodes'],
    queryFn: () => commonService.getCountryCodes(),
    ...QUERY_CONFIG,
  });

  // Determine which country data to use
  const apiCountryCodes = countryCodesResponse?.result || [];
  const hasApiData = apiCountryCodes.length > 0;
  const hasError = !!error && !hasApiData;

  // Use API data if available, otherwise fall back to minimal country list
  const countryCodes = hasApiData ? apiCountryCodes : FALLBACK_COUNTRIES;
  const isUsingFallback = !hasApiData && !isLoading;

  // Memoized lookup for O(1) country resolution
  const countryLookup = useMemo(
    () => createCountryLookup(countryCodes),
    [countryCodes],
  );

  // Log error for debugging (only in development)
  if (hasError && process.env.NODE_ENV === 'development') {
    console.warn('Country API failed, using fallback data:', error);
  }

  // Memoize context value to prevent unnecessary re-renders
  const contextValue = useMemo<CountryContextValue>(
    () => ({
      // Core data
      countryCodes,
      isLoading,
      error: error as Error | null,
      isUsingFallback,

      // Error handling
      retry: () => refetch(),
      hasError,

      // Utility functions bound to current country codes
      validatePhoneInput: sanitizePhoneInput,
      getCountryByCode: (countryCode: string, abbreviation?: string) =>
        findCountryByCodeFromLookup(countryLookup, countryCode, abbreviation),
      getCountryCodeByName: (countryName: string) =>
        findCountryCodeByName(countryCodes, countryName),
      validatePhoneLength: (phoneNumber: string, countryCode: string) =>
        validatePhoneLength(phoneNumber, countryCode, countryCodes),
      getPhoneMaxLength: getMaxPhoneLength,
      getPhoneValidationMessage: getValidationMessage,
      getCountryCodeDisplay: (countryCode: string) =>
        formatCountryCodeDisplay(countryCode, countryCodes),
    }),
    [
      countryCodes,
      countryLookup,
      isLoading,
      error,
      hasError,
      isUsingFallback,
      refetch,
    ],
  );

  return (
    <CountryContext.Provider value={contextValue}>
      {children}
    </CountryContext.Provider>
  );
};

// ================================
// ERROR BOUNDARY COMPONENT
// ================================

interface CountryErrorFallbackProps {
  onRetry: () => void;
  isUsingFallback: boolean;
}

export const CountryErrorFallback: React.FC<CountryErrorFallbackProps> = ({
  onRetry,
  isUsingFallback,
}) => {
  const { t } = useTranslation('common');
  if (!isUsingFallback) return null;

  return (
    <div className='bg-yellow-900/20 border border-yellow-500/30 rounded-lg p-3 mb-4'>
      <div className='flex items-center justify-between'>
        <div className='flex items-center'>
          <div className='w-2 h-2 bg-yellow-400 rounded-full me-2' />
          <span className='text-yellow-300 text-sm'>
            {t('labels.limitedCountryList')}
          </span>
        </div>
        <button
          onClick={onRetry}
          className='text-yellow-300 hover:text-yellow-100 text-sm underline'
        >
          {t('buttons.retry')}
        </button>
      </div>
    </div>
  );
};
